import React from "react"
import { Tag, Typography } from "antd"
import { mutate } from "swr"
import { modules } from "../../config/pages"
import { TStorage } from "../../contexts/RegisterContext"
import { TModuleOptions } from "../../models/storageModel"
import { formatInputNumber } from "../../utils"

const { Text } = Typography

export const mutateMovementQueries = () => {
  mutate(
    (key: any) =>
      (typeof key === 'string' && key.includes('/inventory/movement/')) ||
      (Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/inventory/movement/')),
    undefined,
    { revalidate: true }
  )
}

export const getLists = (mod: string, register: TStorage) => {
  if (mod === modules.RegisterEntry) {
    return {
      itemList: register.itemListEntries?.length ? register.itemListEntries : [],
      pendingList: register.pendingListEntries?.length ? register.pendingListEntries : []
    }
  }
  if (mod === modules.RegisterExit) {
    return {
      itemList: register.itemListExits?.length ? register.itemListExits : [],
      pendingList: register.pendingListExits?.length ? register.pendingListExits : []
    }
  }
  return { itemList: [], pendingList: [] }
}

export const getMovementPrefix = (documentType: string) => {
  switch (documentType) {
    case "PURCHASE_RECEIPT":
      return "COMP"
    case "INITIAL_INVENTORY":
      return "IINV"
    case "PURCHASE_REQUEST":
      return "SOLC"
    case "PURCHASE_ORDER":
      return "ORDC"
    case "PARTNER_CONTRIBUTION":
      return "CONT"
    case "PRODUCTION_ENTRY":
      return "PREN"
    case "SALE_INVOICE":
      return "FACT"
    case "ADJUSTMENT":
      return "AJTE"
    case "WASTE":
      return "PEDA"
    case "PRODUCTION_EXIT":
      return "PRSL"
    case "QUOTATION":
      return "COTI"
    case "TRANSFER":
      return "TRAS"
    case "RETURN_IN":
      return "REIN"
    case "RETURN_OUT":
      return "REOT"    
    default:
      return ""
  }
}

export const getItemOptionLabel = (e: any) => {
  const display = e.display || 'UND'
  const name = e.name || ''
  const customIdStr = e.custom_id && String(e.custom_id).trim() !== '' ? ` - ${e.custom_id}` : ''
  return `[${display}] - ${name}${customIdStr}`
}

export const renderOptionCard = (
  item: any,
  mod: string,
  categoryOptions: TModuleOptions[] = [],
  displayOptions: TModuleOptions[] = [],
  umOptions: TModuleOptions[] = []
) => {
  const isService = item.register_type === 'SERVICE'
  const isFixedAsset = item.register_type === 'FIXED_ASSET'
  const categoryLabel = categoryOptions.find(c => c.key === item.category)?.string_value || item.category
  const displayLabel = displayOptions.find(d => d.key === item.display)?.string_value || 
                       umOptions.find(u => u.key === item.measure_unit)?.string_value || 
                       item.display || 
                       item.measure_unit || 
                       'UND'
  const code = item.custom_id || item.sku

  // Cálculo de existencias totales
  const totalStock = Array.isArray(item.inventory)
    ? item.inventory.reduce((sum: number, inv: any) => sum + (Number(inv.quantity) || 0), 0)
    : (Number(item.quantity) || 0)

  // Precio de referencia según el tipo de movimiento
  const refPrice = mod === modules.RegisterEntry 
    ? (item.purchase_price ?? item.sell_price) 
    : (item.sell_price ?? item.purchase_price)

  return (
    <div style={{ padding: '4px 2px', display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
      {/* Título y Tag de Tipo */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
        <Text strong style={{ fontSize: 13, color: '#1f1f1f', lineHeight: 1.3, wordBreak: 'break-word', flex: 1 }}>
          {item.name}
        </Text>
        <Tag
          color={isService ? 'geekblue' : isFixedAsset ? 'purple' : 'blue'}
          style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 5px', fontWeight: 500, flexShrink: 0 }}
        >
          {isService ? 'Servicio' : isFixedAsset ? 'Activo Fijo' : 'Producto'}
        </Tag>
      </div>

      {/* Badges de Metadatos */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginTop: 2 }}>
        {code && (
          <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#f5f5f5', color: '#595959', padding: '0 4px' }}>
            Ref: <strong>{code}</strong>
          </Tag>
        )}
        {categoryLabel && categoryLabel !== 'VRS' && (
          <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#f5f5f5', color: '#595959', padding: '0 4px' }}>
            Cat: {categoryLabel}
          </Tag>
        )}
        {(item.display || item.measure_unit) && (
          <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#e6f7ff', color: '#096dd9', padding: '0 4px' }}>
            Pres: <strong>{item.display || displayLabel}</strong>
          </Tag>
        )}
        {!isService ? (
          <span
            style={{
              fontSize: 10,
              color: totalStock > 0 ? '#389e0d' : '#cf1322',
              background: totalStock > 0 ? '#f6ffed' : '#fff1f0',
              border: `1px solid ${totalStock > 0 ? '#b7eb8f' : '#ffa39e'}`,
              borderRadius: 3,
              padding: '0 4px',
              fontWeight: 600,
            }}
          >
            Stock: {totalStock} {item.display || item.measure_unit || 'UND'}
          </span>
        ) : (
          <span
            style={{
              fontSize: 10,
              color: '#096dd9',
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: 3,
              padding: '0 4px',
              fontWeight: 600,
            }}
          >
            Gasto directo / Sin inventario físico
          </span>
        )}
        {refPrice !== undefined && refPrice !== null && Number(refPrice) > 0 && (
          <span
            style={{
              fontSize: 10,
              color: mod === modules.RegisterEntry ? '#096dd9' : '#389e0d',
              fontWeight: 600,
              background: mod === modules.RegisterEntry ? '#f0f5ff' : '#f6ffed',
              padding: '0 4px',
              borderRadius: 3,
              border: `1px solid ${mod === modules.RegisterEntry ? '#adc6ff' : '#b7eb8f'}`,
            }}
          >
            {mod === modules.RegisterEntry ? 'Ref. Compra: ' : 'Ref. Venta: '}
            ${formatInputNumber(refPrice)}
          </span>
        )}
      </div>

      {/* Resumen de descripción */}
      {item.description && (
        <div
          style={{
            fontSize: 11,
            color: '#8c8c8c',
            marginTop: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={item.description}
        >
          {item.description}
        </div>
      )}
    </div>
  )
}

export const createOption = (
  item: any,
  mod: string,
  categoryOptions: TModuleOptions[] = [],
  displayOptions: TModuleOptions[] = [],
  umOptions: TModuleOptions[] = []
) => {
  const labelText = getItemOptionLabel(item)
  return {
    label: renderOptionCard(item, mod, categoryOptions, displayOptions, umOptions),
    value: labelText,
    uid: `${item.uid}`,
    key: `${item.uid || item.id}`,
    rawItem: item,
  }
}

export const commonServices = [
  { name: "Transporte / Flete de mercancía", icon: "🚚" },
  { name: "Mano de obra / Cuadrilla", icon: "🔨" },
  { name: "Cargue y descargue", icon: "📦" },
  { name: "Maquila / Procesamiento", icon: "⚙️" },
]
